import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import styled, { keyframes, DefaultTheme } from 'styled-components';
import { Outlet, useLocation } from 'react-router-dom';
import { easing, fluid } from '../styles/motion'; // [FLUID-4 2026-10-12]
import { fluidCascade } from '../components/Fluid'; // [FLUID-4 2026-10-12]
import { restoreScrollTop } from '../services/uiSnapshot'; // [SEAMLESS-UPDATE 2026-08-13]
import Header from '../components/Header';
import BottomNav from '../components/BottomNav';
import NotificationPanel from '../components/NotificationPanel';
import DeletedDrawer from '../components/DeletedDrawer';
import AppAlertsDrawer, { PINNED_CARD_COUNT } from '../components/AppAlertsDrawer'; // [APP-ALERTS 2026-09-04]
import { fetchDeletedItems, fetchAppAlertCount } from '../services/api';
import { loadTrashSeen, saveTrashSeen } from '../services/trashSeen';
import { warmCaches, setWarmPaymentExtraCols } from '../services/prefetch';
import { readPaymentExtraCols } from '../utils/paymentExtraCols'; // [EDIT-COLUMNS 2026-09-28]
import { startRealtime } from '../services/realtime'; // [REALTIME] 2026-06-23
import GoToTop from '../components/GoToTop';
import ErrorBoundary from '../components/ErrorBoundary'; // [LOAD-STATES 2026-09-29]
import { pageEnterStyle } from '../services/pageEnterSide'; // [DOCK-MOTION 2026-09-22]
import { useAuth } from '../contexts/AuthContext';
import { PM_SINGAPORE, SALES_ROW, DUBAI_CONTEST } from '../config/sections'; // [LIQUID-GLASS 2026-11-02] the boards keep their look
import { useIsMobile } from '../hooks/useIsMobile';
import { leadsNoDock } from '../config/sections'; // [TL-REPORT-ONLY 2026-10-19]
import { setAppZoomBoard } from '../utils/appZoom'; // [APP-FIT 2026-10-26]

/** The TV boards: each scales its own stage to the screen and keeps its own design. */
const BOARD_ROUTES = ['/pm-singapore', '/sales-row', '/dubai-contest'];

/* Full-width shell: the left sidebar is gone — navigation lives in the floating
   glass dock (BottomNav) at the bottom-centre, so every screen uses the full
   width of the application. The shell paints the page background (theme.bg) so
   the gutter around the page card reads as a clean frame. Fixed to the viewport
   height so the card below scrolls internally (keeps the sticky table headers
   working). */
/* ── App frame: DESKTOP and MOBILE are SEPARATE shells on purpose ────────────
   AppLayout picks one of these two via useIsMobile(), so a change to the mobile
   frame physically cannot touch the desktop frame, and vice-versa. This is the
   "desktop view" vs "mobile view" split at the shell level:
     • desktop-frame change  → edit DesktopShell
     • mobile-frame change   → edit MobileShell
   Both wrap the SAME MainArea/PageCard below, so page content, the scroll
   container and every page's own logic stay shared — this split is presentation
   only. See [[mobile-shell-dvh-viewport]]. ─────────────────────────────────── */

/* DESKTOP frame — the original in-flow shell: a header row above one flexible
   row that holds the scrolling page card. Sized to the viewport height so the
   card scrolls internally and the header stays put. Unchanged from the
   long-standing desktop layout (desktop renders THIS, untouched). */
/* [LIQUID-GLASS 2026-11-02, user] (preview approved: https://claude.ai/artifact/EfDUMoqsSytqBx3BKVrHdR) The page background: a few
   soft, STILL glows in the app's green, teal and sky blue behind the page card (they show in the gaps around it and faintly
   through the top bar), so the glass of the top bar buttons and the dock has some colour to show. Drawn already soft (plain
   gradients, no live blur, nothing moves), so they cost nothing while a page scrolls. Phones get a weaker glow. Not on the
   PM Singapore / Sales ROW / Dubai boards, which keep their own design ([data-glow] is left off there). */
const glowBg = (p: { theme: DefaultTheme }, strength = 1) => {
  const d = p.theme.colorScheme === 'dark';
  const a = (x: number) => (x * strength).toFixed(3);
  const g1 = d ? `rgba(16,185,129,${a(0.22)})` : `rgba(52,211,153,${a(0.34)})`;
  const g2 = d ? `rgba(20,184,166,${a(0.16)})` : `rgba(45,212,191,${a(0.26)})`;
  const g3 = d ? `rgba(56,189,248,${a(0.14)})` : `rgba(125,211,252,${a(0.28)})`;
  return `radial-gradient(38% 46% at 8% 10%, ${g1}, transparent 70%), radial-gradient(34% 40% at 94% 6%, ${g3}, transparent 72%), `
    + `radial-gradient(42% 50% at 88% 96%, ${g2}, transparent 72%), radial-gradient(30% 36% at 10% 98%, ${g3}, transparent 70%), ${p.theme.bg}`;
};

const DesktopShell = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  /* [APP-FIT 2026-10-26] The window's height in page px: under the app zoom (utils/appZoom) a plain 100vh is zoomed too and
     filled only 71 % of a 1366 px laptop's height. */
  height: calc(100vh / var(--app-zoom, 1));
  background: ${(p) => p.theme.bg};
  /* [LIQUID-GLASS 2026-11-02, user] */
  &[data-glow] { background: ${(p) => glowBg(p)}; }
`;

/* MOBILE frame — same header-row + scrolling-card structure, but sized to the
   VISIBLE viewport (100dvh, not 100vh) and overflow-capped. Why it differs: on a
   phone the URL bar makes the visible viewport shrink/grow, and 100vh is the
   URL-bar-HIDDEN (taller) height — that let the whole document scroll and dragged
   the in-flow Header off the top while the fixed dock stayed (you'd see the top
   OR the bottom, never both). 100dvh tracks the current visible height and
   overflow:hidden caps the frame, so Header + dock stay pinned and only the
   PageCard scrolls. Pairs with the html,body scroll lock in GlobalStyles (@≤768),
   which is the actual guard against the URL-bar overshoot. All future MOBILE-frame
   changes belong HERE — they can't reach DesktopShell. */
const MobileShell = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  /* [PHONE-HEADER-FIT 2026-09-29, user] Without an explicit column the grid's one track was
     'auto', sized to the header's min-content width (brand + nowrap title + actions = 453px for
     an admin), so on 360–450px phones the right edge was CLIPPED by overflow:hidden below: the
     Profile / Sign out button and the ends of every page toolbar. minmax(0,1fr) pins the column
     to the screen width; the header's title then shortens with its existing ellipsis. */
  grid-template-columns: minmax(0, 1fr);
  height: 100vh; /* fallback for browsers without dvh */
  height: 100dvh;
  overflow: hidden;
  background: ${(p) => p.theme.bg};
  /* [LIQUID-GLASS 2026-11-02, user] */
  &[data-glow] { background: ${(p) => glowBg(p, 0.7)}; }
`;

/* [FLUID-4 2026-10-12] The page entrance, split in two so the content is readable at once (fade: 200 ms, fast start)
   while the drift settles on the dock's spring (340 ms). Ends on translateY(0) with fill both, exactly as the old
   single keyframe did — PageFade has always stayed a containing block after its entrance and pages may rely on it. */
const pageFadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const pageDrift = keyframes`
  from { transform: translate(var(--page-enter-x, 0px), var(--page-enter-y, 8px)); }
  to   { transform: translateY(0); }
`;

/* Quick fade-up on each tab switch so a section appears to "snap" in rather
   than blink — pairs with the instant API cache for a fast, premium feel. */
const PageFade = styled.div<{ $blocks: boolean }>`
  /* [STICKY-HEAD 2026-06-26] Complete the height chain so a page can freeze a
     block above its table and scroll only the rows below. PageCard is a definite
     height (100% of the stretched MainArea grid track); without this line PageFade
     was height:auto, so a page's height:100% root (PaymentsPage's Container)
     collapsed to auto and grew to fit ALL content — the whole card scrolled and
     nothing froze. Filling PageFade to the card's content box fixes that. Benign
     for every other page: their roots use min-height:100% (a floor that just fills
     the card, never clips), and overflow stays visible so tall pages still scroll
     in PageCard exactly as before. */
  height: 100%;
  /* Smooth ease-out so a tab switch glides in rather than snapping. Kept short
     (≈ motion.ts "page mount" band) so the switch reads as instant — a longer
     fade is the one thing that makes an otherwise-cached tab feel laggy.
     No persistent will-change: the keyframe animation auto-composites while it
     runs, and the element stays mounted afterwards, so leaving the hint on would
     just pin an idle compositor layer (see motion.ts performance rules). */
  /* [DOCK-MOTION 2026-09-22] After a dock click this element carries --page-enter-x/-y on its own
     inline style (services/pageEnterSide.ts), so the new page drifts in 16px from the side of the
     tab that was clicked, in step with the green lozenge. Any other navigation finds them unset and keeps the 6px rise. 240ms on the
     easeOutExpo curve: most of the fade is done in the first ~60ms, so the switch still reads as
     instant; the long soft tail is what makes it feel smooth rather than cut. The resting state
     (to) is unchanged. */
  /* [FLUID-4 2026-10-12, user] "fluid animation … tab change": the drift now settles on the dock's spring (same curve
     as the green lozenge) and the fade is its own quick animation; second line = fallback without linear(). */
  animation: ${pageFadeIn} ${fluid.fade} ${easing.entrance} both, ${pageDrift} ${fluid.in} ${easing.entrance} both;
  animation: ${pageFadeIn} ${fluid.fade} ${easing.entrance} both, ${pageDrift} ${fluid.in} ${fluid.spring} both;
  /* [FLUID-4 2026-10-12, user] …and the page's main blocks (title, toolbar, KPI strip, table…) rise in one after
     another, all in place by ~0.5 s — except on the PM Singapore and Team ROW boards, which keep their own design. */
  ${(p) => (p.$blocks ? fluidCascade('> * > *', 0, 20, 6) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}
`;

/* The gutter around the page card. Holds the page background so a consistent
   frame shows on all four sides of every section. It never scrolls itself —
   the card inside does — so the frame stays put while content moves. */
const MainArea = styled.div`
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: ${(p) => p.theme.bg};
  padding: 16px;

  /* Tighter gutter on phones so the data card gets nearly the full screen width
     (wide tables need every pixel). Desktop padding is unchanged. */
  @media (max-width: 768px) {
    padding: 8px;
  }
  /* [LIQUID-GLASS 2026-11-02, user] */
  [data-glow] & { background: transparent; } /* the shell's glow shows in the gaps around the card */
`;

/* The framed page surface. A single rounded, bordered, softly-elevated card
   that wraps EVERY section — so the consistent border lives in one place
   instead of being re-applied per page. It is the scroll container (overflow
   auto), which both clips its content to the rounded corners and keeps the
   tables' sticky headers pinned to the top of the card. */
const PageCard = styled.div<{ $flush?: boolean }>`
  position: relative;
  height: 100%;
  /* [STICKY-HEAD 2026-06-26] $flush page (Payments) scrolls its table internally,
     so the card must NOT also be a scroll container — otherwise a redundant
     full-height card scrollbar shows beside the frozen header and scrolls nothing.
     Clip instead; every other page still scrolls in the card.
     [SCROLLBAR 2026-07-08] Non-flush cards use overflow-y:SCROLL (not auto) so the
     vertical scrollbar is ALWAYS visible on desktop, exactly like the deployed
     build — never auto-hidden. Horizontal stays auto (only wide tables get it). */
  overflow-x: ${(p) => (p.$flush ? 'hidden' : 'auto')};
  overflow-y: ${(p) => (p.$flush ? 'hidden' : 'scroll')};
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.line};
  border-radius: ${(p) => p.theme.radius};
  box-shadow: ${(p) => p.theme.shadow};
  /* Breathing room at the end of the scroll so the floating dock never covers
     the last rows of content (the card's rounded bottom corners still show
     beside the centred dock).
     [STICKY-HEAD 2026-06-26] A page that scrolls INTERNALLY (Payments — frozen
     toolbar/summary/header above its OWN scrolling table) doesn't scroll the card
     at all, so this strip is just dead height wasted below the table. Such a page
     sets $flush to reclaim it for rows and owns the dock clearance at the end of
     its own scroll (see PaymentsPage's TableScroll padding-bottom) instead. */
  padding-bottom: ${(p) => (p.$flush ? '0' : '88px')};
  transition: background 320ms cubic-bezier(0.4, 0, 0.2, 1), border-color 320ms cubic-bezier(0.4, 0, 0.2, 1);
  /* [LIQUID-GLASS 2026-11-02, user] */
  /* the card stays solid; a light top edge and a softer shadow */
  [data-glow] & { box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.9)')}, 0 1px 2px rgba(0,0,0,0.05), 0 18px 40px -18px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.6)' : 'rgba(22,25,23,0.22)')}; }
`;

const AppLayout: React.FC = () => {
  const mainRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  /** Routes that render their own bottom-edge content the pill would cover. */
  const hidesGoToTop = location.pathname === '/eom' || location.pathname.startsWith('/eom/');

  /* [SEAMLESS-UPDATE 2026-08-13] The far side of an update reload: put the page card back where
     it was. A no-op on every ordinary load — the snapshot is only written by UpdateBanner, and
     it is consumed once. Mount-only on purpose: restoring on every navigation would fight the
     user's own scrolling. */
  useEffect(() => { restoreScrollTop(); }, []);
  // Trash is admin-only — non-admins never poll its count or mount the drawer.
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  // [NOTIF-GATE 2026-07-15] Mirrors the header's gate; keep the two in step or the bell
  // hides while its poll keeps running.
  // [NOTIF-ADMIN-ONLY 2026-09-16, user] Was admin + senior — now ADMIN ONLY, matching the
  // header and the server: the whole /api/notifications router is requireAdmin, so a senior
  // left polling here would collect a 403 every 60 seconds for a bell they can no longer see.
  const canSeeNotifs = isAdmin;
  // [PM-SG 2026-08-11] The shared board account — see the dock note below.
  const isPasswordAccount = !!user?.is_password_account;
  // [LEADS-NO-DOCK 2026-10-14, user] "for the shivam and harshit they only see the lead management button but it is of no need
  // as they only have single page so remove that tab switch from the bottom for them": the Leads type (not admin) — like
  // the board account — has ONE page, so no dock (and no Go-to-Top pill). [TL-REPORT-ONLY 2026-10-19] only while they have ONE tab:
  // a team leader (Lead Management + Team Sameer Report) gets the dock to move between the two (config/sections leadsNoDock).
  const isLeadsOnly = leadsNoDock(user);
  // [MOBILE 2026-07-07] Hide the floating "Go to Top" pill on phones — it stacked
  // right above the bottom dock and covered the last rows of content. Desktop keeps it.
  const isMobile = useIsMobile();
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [isTrashOpen, setIsTrashOpen] = useState(false);
  // Keys (`source-id`) of every row currently in the trash, plus the set the
  // admin has acknowledged via "Mark as Read". The badge shows how many trashed
  // rows are NOT yet acknowledged, so it clears once reviewed and only re-appears
  // when something new is deleted. See services/trashSeen.ts.
  const [trashKeys, setTrashKeys] = useState<string[]>([]);
  const [trashSeen, setTrashSeen] = useState<Set<string>>(() => loadTrashSeen());
  const trashCount = trashKeys.reduce((n, k) => (trashSeen.has(k) ? n : n + 1), 0);
  /* [APP-ALERTS 2026-09-04] Faults, admin-only. Deliberately NOT given the Bin's
     acknowledged-keys treatment: a fault is not "read", it is fixed or it is not, and the
     count comes straight from what is still wrong. Letting it be marked as seen would let a
     broken feed sit at zero. */
  const [isAlertsOpen, setIsAlertsOpen] = useState(false);
  const [alertsCount, setAlertsCount] = useState(0);

  // The PageCard is the scroll container and stays mounted across tab switches,
  // so it keeps the previous section's scroll offset. Reset it to the top on
  // every route change so each tab opens as a fresh view from the top.
  // useLayoutEffect runs before paint, so the new section never flashes
  // mid-scroll before snapping up.
  useLayoutEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0 });
  }, [location.pathname]);

  /* [APP-FIT 2026-10-26] The app zoom is off while a TV board is open (it fits its own stage to the screen — both would
     scale it twice) and back on when it closes; before paint, so neither is ever drawn at the other's size. */
  const onBoard = BOARD_ROUTES.some((r) => location.pathname === r || location.pathname.startsWith(r + '/'));
  useLayoutEffect(() => { setAppZoomBoard(onBoard); }, [onBoard]);
  useLayoutEffect(() => () => setAppZoomBoard(false), []);

  const refreshNotifCount = useCallback(() => {
    // [NOTIF-GATE 2026-07-15] Don't poll for users who can't see the bell — it's a
    // 60s request forever for UI they'll never be shown. Same guard style as
    // refreshTrashCount below.
    if (!canSeeNotifs) return;
    fetch('/api/notifications/pending')
      .then((r) => r.json())
      .then((data) => setNotifCount(Array.isArray(data) ? data.length : 0))
      .catch(() => {});
  }, [canSeeNotifs]);

  const refreshAlertsCount = useCallback(() => {
    if (!isAdmin) return; // /api/app-alerts is admin-only
    fetchAppAlertCount()
      // [LIVE-CONNECTIONS-COUNT 2026-09-30, user] + the live connections card, which is always shown.
      .then(({ open }) => setAlertsCount(open + PINNED_CARD_COUNT))
      .catch(() => {});
  }, [isAdmin]);

  const refreshTrashCount = useCallback(() => {
    if (!isAdmin) return; // /api/deleted is admin-only
    fetchDeletedItems()
      .then((items) => {
        const keys = items.map((i) => `${i.source}-${i.id}`);
        setTrashKeys(keys);
        // Prune the acknowledged set down to rows that are still in the trash, so
        // it can't grow unbounded and a restored-then-redeleted row re-surfaces
        // as unread. Persist only when something actually dropped off.
        setTrashSeen((prev) => {
          if (prev.size === 0) return prev;
          const present = new Set(keys);
          const next = new Set<string>();
          prev.forEach((k) => { if (present.has(k)) next.add(k); });
          if (next.size === prev.size) return prev;
          saveTrashSeen(next);
          return next;
        });
      })
      .catch(() => {});
  }, [isAdmin]);

  // "Mark as Read": acknowledge every row currently in the trash so the badge
  // clears. Takes the keys the drawer is actually showing (most up-to-date) and
  // unions them with what we've polled, so nothing is missed.
  const markTrashRead = useCallback((keysFromDrawer: string[] = []) => {
    setTrashSeen((prev) => {
      const next = new Set(prev);
      trashKeys.forEach((k) => next.add(k));
      keysFromDrawer.forEach((k) => next.add(k));
      saveTrashSeen(next);
      return next;
    });
  }, [trashKeys]);

  useEffect(() => {
    refreshNotifCount();
    refreshTrashCount();
    refreshAlertsCount();
    const interval = setInterval(() => {
      if (document.hidden) return; // idle/background tab shouldn't poll
      refreshNotifCount();
      refreshTrashCount();
      refreshAlertsCount();
    }, 60000);
    return () => clearInterval(interval);
  }, [refreshNotifCount, refreshTrashCount, refreshAlertsCount]);

  // Once the first screen is up, quietly warm the other sections' caches on idle
  // so switching into them is instant. Read-only, best-effort, runs once.
  // [REALTIME] 2026-06-23 — also start the single shared data-version poller, so
  // every page updates within a few seconds of any DB change (idempotent).
  useEffect(() => {
    // [EDIT-COLUMNS 2026-09-28] An admin's saved Payments columns ride along on the Payments warm.
    if (user?.is_admin) setWarmPaymentExtraCols(readPaymentExtraCols(user.id));
    warmCaches(); startRealtime();
  }, []); // once per sign-in, as before

  // Pick the frame for this viewport. Mobile and desktop render SEPARATE shell
  // components (DesktopShell / MobileShell above), so the two frames are isolated:
  // editing one can never affect the other. Everything inside is shared.
  const Frame = isMobile ? MobileShell : DesktopShell;
  const glow = ![PM_SINGAPORE.path, SALES_ROW.path, DUBAI_CONTEST.path].some((p) => location.pathname.startsWith(p)); // [LIQUID-GLASS]

  return (
    <>
      <Frame data-glow={glow ? '' : undefined}>
        <Header
          onNotifToggle={() => setIsNotifOpen((p) => !p)}
          notifCount={notifCount}
          onTrashToggle={() => setIsTrashOpen((p) => !p)}
          trashCount={trashCount}
          onAlertsToggle={() => setIsAlertsOpen((p) => !p)}
          alertsCount={alertsCount}
        />
        <MainArea>
          {/* [STICKY-HEAD 2026-06-26] These pages scroll their table internally (frozen
              toolbar/header), so the card itself never scrolls there — drop the 88px
              dock-clearance strip + outer scrollbar so those reclaimed pixels show rows.
              Each such page owns its own dock clearance via its TableScroll padding. */}
          <PageCard
            ref={mainRef}
            data-page-scroll=""
            $flush={['/payments', '/delayed-payments', '/month-on-month', '/new-sales', '/refunds', '/disputes', '/inventory', '/my-inventory', '/user-management', '/my-sales',
              /* [2026-08-31, user] "there is one scroller for the table, but we have the layout
                 for the complete page scroll, why?" — Sales Scoreboard was MISSING from this
                 list. Its own Container is height:100% + overflow:hidden with the table in a
                 flex:1 scroller, so the card was drawing the exact redundant scrollbar the
                 note above warns about: two tracks side by side, the outer one scrolling
                 nothing. It owns its dock clearance already (ScrollContainer padding-bottom
                 56px), so it fits this list's contract without further change. */
              '/sales-scoreboard',
              /* [CAMPAIGN-HISTORY 2026-09-04, user] "remove the full page scroller from the
                 campaign history page and all the 3 sections left right AND middle will have its
                 own vertical scroll". Each of its three columns is its own scroller, so the card
                 must not scroll too — otherwise the page scrolls as a whole and the columns never
                 reach their own overflow. It owns its dock clearance in CampaignHistoryPage's
                 Page padding-bottom, per this list's contract. */
              '/campaign-history',
              /* [LEAD-FULL-HEIGHT 2026-10-14, user] "in the lead management the table should show just like it is showing
                 in payment tab and when i want to see the end data then it will come above the tab switch": the header
                 stays, the table fills the card and scrolls by itself, its own bottom pad lifts the last row above the
                 dock (LeadAssignmentPage TableScroll). */
              '/lead-assignment',
              /* [TEAM-SAMEER 2026-10-15] the same layout: the top bar stays, the table scrolls in its own box (TeamSameerReportPage). */
              '/team-sameer-report',
              /* [LGP 2026-10-15] the same layout: each view's table scrolls in its own box (LeadGeneratorPerformancePage). */
              '/lead-generator-performance',
              /* [PIPELINE-FULL-HEIGHT 2026-10-22, user] the 4 columns fill the screen down to just above the dock, each
                 scrolling by itself (SalesPipelinePage Page padding-bottom). */
              '/sales-pipeline'].some(
              (r) => location.pathname === r || location.pathname.startsWith(r + '/')
            )}
          >
            {/* [RD-PILL 2026-09-10] /refunds and /disputes share ONE key: RefundsDisputesPage serves
                both and slides between them, and a new key here would remount it (no slide, both
                tables refetched). Every other route keeps its own key, so its own fade-in. */}
            <PageFade
              key={location.pathname.replace(/^\/disputes(?=\/|$)/, '/refunds')}
              style={pageEnterStyle() /* [DOCK-MOTION 2026-09-22] */}
              $blocks={!onBoard /* [FLUID-4 2026-10-12] [DUBAI 2026-10-19] the TV boards keep their own design */}
            >
              {/* [LOAD-STATES 2026-09-29, user] A crash in one tab shows that tab's own "This section
                  hit a problem" screen instead of blanking the whole dashboard. Inside PageFade, so a
                  tab switch (PageFade's key) always starts the next tab with a clean boundary. */}
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </PageFade>
          </PageCard>
        </MainArea>
      </Frame>

      {/* [PM-SG 2026-08-11] The credentials account gets NO portal dock. It can reach exactly
          one section, so the dock would be a floating pill row containing a single pill — and
          the board it opens onto carries its own navigation on the bottom edge, which the two
          would sit on top of. Same reasoning as the header early-return for these sessions. */}
      {!isPasswordAccount && !isLeadsOnly && <BottomNav />}
      {/* [EOM 2026-09-03, user] The EOM page opts OUT of the floating pill: its Dashboard view
          ends in a full-width green total strip, and the pill sat on top of it. Excluded here
          rather than in GoToTop itself, which every other page still uses unchanged. */}
      {!isMobile && !isPasswordAccount && !isLeadsOnly && !hidesGoToTop && <GoToTop scrollRef={mainRef} threshold={300} />}
      {/* [NOTIF-GATE 2026-07-15] Don't even mount the panel for users without the
          bell — matches how the admin-only DeletedDrawer below is gated, so the
          drawer can't be reached by any other route into state. */}
      {canSeeNotifs && (
        <NotificationPanel open={isNotifOpen} onClose={() => { setIsNotifOpen(false); refreshNotifCount(); }} />
      )}
      {/* [APP-ALERTS 2026-09-04] Same admin gate as the Bin below — never mount the drawer for
          a user who cannot reach /api/app-alerts, so no route into state can open it. */}
      {isAdmin && (
        <AppAlertsDrawer
          open={isAlertsOpen}
          onClose={() => { setIsAlertsOpen(false); refreshAlertsCount(); }}
          onCounts={setAlertsCount}
        />
      )}
      {isAdmin && (
        <DeletedDrawer
          open={isTrashOpen}
          onClose={() => { setIsTrashOpen(false); refreshTrashCount(); }}
          onChanged={refreshTrashCount}
          unread={trashCount}
          onMarkRead={markTrashRead}
        />
      )}
    </>
  );
};

export default AppLayout;
